import { isTextInput } from '../../utils/input.js';

/**
 * When `deadZone: true`, a touch-based pointerdown that does not pass the DEAD_ZONE threshold
 * before this many ms has elapsed will be ignored. This is because iOS will trigger the text
 * magnifying glass after roughly 1s, and the only way for us to prevent that would be to
 * unconditionally set `-webkit-user-select: none` on the element. However, it might be nice to
 * allow the user to select text instead of dragging, so we'll only prevent the magnifying glass if
 * the drag has passed the deadZone.
 */
const TOUCH_TIMEOUT = 900;
/**
 * When `deadZone: true`, the pointer has to move this many px before a drag starts.
 *
 * Determine the dead zone based on the pointerType, with the idea that
 * less accurate pointer types need more wiggle room. Also, the threshold at which iOS
 * fires a pointercancel seems to be right around 10px, and we want to make sure
 * pointercancel has a chance to fire before we think the user is dragging.
 */
const DEAD_ZONE_BY_POINTER_TYPE = { mouse: 8, pen: 16, touch: 16 };
/**
 * The number of pointermove events that need to occur outside the initial text input for us
 * to allow a drag to start. Pointermove events fire faster than the browser has a chance to select
 * text, so we make sure to give the browser enough time to select text and cancel the drag.
 */
const MOVEMENT_EVENTS_OUTSIDE_TEXT_INPUT = 5;
/**
 * Get the dead zone value for the given pointerType.
 *
 * Use this function instead of DEAD_ZONE_BY_POINTER_TYPE object directly, because MDN states:
 *
 * > If the device type cannot be detected by the browser, the value can be an empty string ("").
 * > If the browser supports pointer device types other than those listed above, the value should
 * > be vendor-prefixed to avoid conflicting names for different types of devices.
 *
 * If the pointerType is not recognized, we default to the mouse dead zone.
 */
function getDeadZone(pointerType) {
    return DEAD_ZONE_BY_POINTER_TYPE[pointerType] ?? DEAD_ZONE_BY_POINTER_TYPE.mouse;
}
/**
 * If the element is a text input or textarea, return it as-is. Otherwise, if the element is
 * contentEditable, look for the furthest ancestor that is also contentEditable and return it.
 * If no such ancestor exists, or the element is not a text input, return null.
 */
function getFurthestEditableAncestorOrSelf(el) {
    if (isTextInput(el))
        return el;
    if (!el.isContentEditable)
        return null;
    while (el && el.parentElement?.isContentEditable) {
        el = el.parentElement;
    }
    return el;
}
/** Shared `onGestureStart` actions. Stateless, so a single instance serves every caller. */
const GESTURE_START_ACTIONS = {
    // `accept` is overloaded, so its parameter needs an explicit type. Both overloads share this one
    // implementation, which just carries the options through to the `start` result.
    accept: ((options) => ({
        type: 'start',
        ...options,
    })),
    cancel: () => ({ type: 'cancel' }),
};

export { GESTURE_START_ACTIONS, MOVEMENT_EVENTS_OUTSIDE_TEXT_INPUT, TOUCH_TIMEOUT, getDeadZone, getFurthestEditableAncestorOrSelf };
//# sourceMappingURL=use-drag.utils.js.map
